import { LocalStorage } from "./LocalStorage";

export const HASURA_CLAIMS_NAMESPACE = "https://hasura.io/jwt/claims";

export type AppSessionClaims = Record<string, unknown> & {
  token_use?: string;
  terms_accepted?: boolean;
  active_user_id?: string;
  session_user_id?: string;
};

/** Decode a JWT payload without verification (display/routing decisions only). */
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function getAppSessionClaims(): AppSessionClaims | null {
  const token = LocalStorage.getToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token) as AppSessionClaims | null;
  if (payload?.token_use !== "vetnebula_session") return null;
  return payload;
}

/**
 * Whether the stored session accepted the Terms of Use. The API is the source of
 * truth: an unaccepted session only gets the restricted `pending_terms` Hasura
 * role, so this is a UI hint, not the enforcement point.
 */
export function sessionAcceptedTerms(): boolean {
  return getAppSessionClaims()?.terms_accepted === true;
}

/**
 * True when a session token exists but predates the terms claim. Such tokens
 * are treated as not accepted so the user is asked once and re-issued a token.
 */
export function sessionHasTermsClaim(): boolean {
  const claims = getAppSessionClaims();
  return claims !== null && typeof claims.terms_accepted === "boolean";
}
